import React from "react";
import * as yup from 'yup';
import Title from "../../../components/Title/Title";
import { Country, DataTitle } from 'referenceData';
import {
    FormLayout,
} from "@piggybank/core";
import {
    Form,
    FormFeedback
} from "@piggybank/form";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";
import { useHistory } from "react-router-dom";
import { WithI18nProgressIndicator } from "components/WithI18nPiggybank";
import { ButtonWithBack } from "components/ButtonWithBack";
import i18n from 'i18next';
import { timeAtLeast18Years } from "utils/date";
import { rePrevious } from 'utils/util';
import { getUserSessionDataByKey, setUserSessionDataByKey } from 'storage/userStorage';
import { useTagging, validationTagging } from "tealiumTrack";
import { CHINA, PASSPORT, EEP, NO, YES } from '../../../referenceData/constants';
import {
    AboutYouSection,
    ContactDetailsSection,
    IdentityDetailsSection,
} from './PersonalDetailsSections';
let addAnotherCount = 1;

const CHARACTERS_AT_MOST = 'ICO.PERSONAL.CHARACTERS_AT_MOST';
const PHONE_NUMBER_INVALID = 'ICO.PERSONAL.PHONE_NUMBER_INVALID';
const COMMON_SPECIAL_ERROR = 'ICO.COMMON.SPECIAL_ERROR';
const TAG_PAGE_URL = '/forms/personal-information';
const TAG_PAGE_NAME = 'pws:forms:personal information';

const getPrcIdGenderDigit = (value) => parseInt(value.slice(16, 17), 10);

const schema = yup.object().shape({
    email: yup
        .string()
        .email(() => i18n.t('ICO.PERSONAL.EMAIL_ADDRESS_FORMAT_ERROR'))
        .max(35, ({ max }) => i18n.t(CHARACTERS_AT_MOST, { filedText: i18n.t('ICO.PERSONAL.EMAIL_ADDRESS'), max: max }))
        .required(() => i18n.t('ICO.PERSONAL.EMAIL_ADDRESS_ERROR'))
        .matches(/^[^\u4300-\u9fa5]*$/, () => i18n.t('ICO.PERSONAL.EMAIL_ADDRESS_INCLUDE_CHINESE_CHAR_ERROR'))
        .matches(/^[0-9a-zA-Z!#$%*\-_.@]{1,36}$/, () => i18n.t('ICO.COMMON.SPECIAL_MAIL_ERROR')),
    phoneNumber: yup
        .object().shape({
            raw: yup
                .string(),
            value: yup
                .string()
                .required(() => i18n.t('ICO.PERSONAL.PHONE_NUMBER_ERROR'))
                .when('countryCode', {
                    is: val => val === 'CN',
                    then: yup.string()
                        .notOneOf(['invalid', 'incomplete'], () => i18n.t(PHONE_NUMBER_INVALID))
                        .test('is-cn-mobile-number', () => i18n.t(PHONE_NUMBER_INVALID), function () {
                        const { raw } = this.parent;
                        return !raw || /^\d{11}$/.test(raw);
                    }),
                    otherwise: yup.string()
                        .notOneOf(['invalid', 'incomplete'], () => i18n.t('ICO.PERSONAL.PHONE_NUMBER_INVALID_NORMAL'))
                }),
            countryCode: yup
                .string(),
        }),
    gender: yup
        .string()
        .matches(/(Male|Female)/)
        .required(() => i18n.t('ICO.PERSONAL.GENDER_ERROR')),
    title: yup
        .string()
        .matches(/(Mr|Mrs|Ms|Miss|Others)/)
        .required(() => i18n.t('ICO.PERSONAL.ABOUT_YOU_TITLE_ERROR')),
    firstName: yup
        .string()
        .max(15, ({ max }) => i18n.t(CHARACTERS_AT_MOST, { filedText: i18n.t('ICO.PERSONAL.FIRST_NAME'), max: max }))
        .required(() => i18n.t('ICO.PERSONAL.FIRST_NAME_ERROR'))
        .matches(/^[\u4300-\u9fa5A-Za-z0-9.\-/, ]*$/, () => i18n.t(COMMON_SPECIAL_ERROR)),
    lastName: yup
        .string()
        .max(15, ({ max }) => i18n.t(CHARACTERS_AT_MOST, { filedText: i18n.t('ICO.PERSONAL.LAST_NAME'), max: max }))
        .required(() => i18n.t('ICO.PERSONAL.LAST_NAME_ERROR'))
        .matches(/^[\u4300-\u9fa5A-Za-z0-9.\-/, ]*$/, () => i18n.t(COMMON_SPECIAL_ERROR)),
    dateOfBirth: yup
        .date()
        .transform(function (value, originalValue) {
            if (this.isType(originalValue)) {
                return value;
            }
            return DateTime.fromISO(originalValue).isValid ? value : new Date('');
        })
        .typeError(() => i18n.t('ICO.PERSONAL.DATE_OF_BIRTH_TYPEERROR'))
        .min('1900-01-01', () => i18n.t('ICO.PERSONAL.DATE_OF_BIRTH_TYPEERROR'))
        .required(() => i18n.t('ICO.PERSONAL.DATE_OF_BIRTH_ERROR')),
    countryOfBirth: yup
        .string()
        .required(() => i18n.t('ICO.PERSONAL.COUNTRY_OF_BIRTH_ERROR')),
    documentType: yup
        .string()
        .required(() => i18n.t('ICO.PERSONAL.PERMIT_TYPE_ERROR')),
    placeOfIssue: yup
        .string()
        .when("documentType", {
            is: val => val === PASSPORT,
            then: yup.string().required(() => i18n.t('ICO.PERSONAL.PERMIT_COUNTRY_ERROR'))
        }),
    permitNumber: yup
        .string()
        .test('permit-number-required', () => i18n.t('ICO.PERSONAL.PERMIT_NUMBER_ERROR'), function (value) {
            const { documentType } = this.parent;
            if (documentType) {
                return !!value;
            }
            return true;
        })
         .test('permit-number-format-by-nationality', function (value) {
            const { documentType, nationality } = this.parent;
            if (!documentType || !value) {
                return true;
            }
            if (documentType === EEP) {
                return /^[A-Za-z0-9]{9}$/.test(value)
                    ? true
                    : this.createError({ message: i18n.t('ICO.PERSONAL.PERMIT_NUMBER_INVALID') });
            }
            if (documentType === PASSPORT && nationality === CHINA) {
                return /^[A-Za-z0-9]{9}$/.test(value)
                    ? true
                    : this.createError({ message: i18n.t('ICO.PERSONAL.PERMIT_NUMBER_INVALID') });
            }
            if (documentType === PASSPORT) {
                return /^[A-Za-z0-9]{2,12}$/.test(value)
                    ? true
                    : this.createError({ message: i18n.t('ICO.PERSONAL.PERMIT_NUMBER_INVALID_NORMAL') });
            }
            return true;
        }),
    dateOfExpiry: yup
        .date()
        .when("documentType", {
            is: val => val,
            then: yup
                .date()
                .transform(function (value, originalValue) {
                    if (this.isType(originalValue)) {
                        return value;
                    }
                    return DateTime.fromISO(originalValue).isValid ? value : new Date('');
                })
                .typeError(() => i18n.t('ICO.PERSONAL.DATE_OF_EXPIRY_INVALID_TYPE'))
                .required(() => i18n.t('ICO.PERSONAL.DATE_OF_EXPIRY_REQUIRED'))
                .test('is-future-date', () => i18n.t('ICO.PERSONAL.DATE_OF_EXPIRY_INVALID'), function (value) {
                    return value && (DateTime.fromJSDate(value).startOf('day') >= DateTime.now().startOf('day'));
                })
                .test('is-at-least-180-days', () => i18n.t('ICO.PERSONAL.DATE_OF_EXPIRY_INVALID_TOO_SOON'), function (value) {
                    return value && DateTime.fromJSDate(value).diffNow('days').days > 179;
                }),
        }),
    nationality: yup
        .string()
        .test('china-must-be-single-nationality', () => i18n.t('ICO.PERSONAL.NATIONALITY_DESC'), function (value) {
            const { hasOtherNationality } = this.parent;
            return !(value === CHINA && hasOtherNationality === YES);
        })
        .required(() => i18n.t('ICO.PERSONAL.NATIONALITY_INVALID')),
    prcIdNumber: yup
        .string()
        .when(["nationality", "dateOfBirth", "gender"], {
            is: (nationality) => nationality === CHINA,
            then: yup.string()
                .required(() => i18n.t('ICO.PERSONAL.PRC_IDENTITY_NUMBER_REQUIRED'))
                .matches(/^[0-9X]{18}$/, () => i18n.t('ICO.PERSONAL.PRC_IDENTITY_NUMBER_INVALID'))
                .test('matches-date-of-birth', () => i18n.t('ICO.PERSONAL.PRC_IDENTITY_NUMBER_INVALID_DOB'), function (value) {
                    if (!value || !this.parent.dateOfBirth) {
                        return false;
                    }
                    const dob = DateTime.fromJSDate(this.parent.dateOfBirth).toFormat('yyyyMMdd');
                    return value.substring(6, 14) === dob;
                })
                .test('matches-gender', () => i18n.t('ICO.PERSONAL.PRC_IDENTITY_NUMBER_INVALID_GENDER'), function (value) {
                    if (!value || !this.parent.gender) {
                        return false;
                    }
                    const genderDigit = getPrcIdGenderDigit(value);
                    const isMale = this.parent.gender === 'Male';
                    return (isMale && genderDigit % 2 === 1) || (!isMale && genderDigit % 2 === 0);
                })
        }),
    chineseLegalName: yup
        .string()
        .when("nationality", {
            is: val => val === CHINA,
            then: yup
                .string()
                .required(() => i18n.t('ICO.PERSONAL.CHINESE_LEGAL_NAME_REQUIRED'))
                .matches(/^[\u4e00-\u9fa5\u00B7]{1,20}$/, () => i18n.t('ICO.PERSONAL.CHINESE_LEGAL_NAME_INVALID'))
        }),
    hasOtherNationality: yup
        .string()
        .when('nationality', {
            is: val => val !== CHINA,
            then: yup.string()
                .matches(/(yes|no)/)
                .required(() => i18n.t('ICO.PERSONAL.NATIONALITY_ERROR')),
            otherwise: yup.string().nullable()
        }),
    nationality2: yup
        .string()
        .when("hasOtherNationality", {
            is: val => val === YES,
            then: yup.string().required(() => i18n.t('ICO.PERSONAL.OTHER_NATIONALITY_ERROR'))
        })
        .test('other-nationality-cannot-be-china', () => i18n.t('ICO.PERSONAL.NATIONALITY_DESC'), function (value) {
            const { hasOtherNationality, nationality } = this.parent;
            return !(hasOtherNationality === YES && nationality !== CHINA && value === CHINA);
        }),
    nationality3: yup
        .string()
        .when("hasOtherNationality", {
            is: val => val === YES && addAnotherCount === 2,
            then: yup.string().required(() => i18n.t('ICO.PERSONAL.OTHER_NATIONALITY_ERROR'))
        })
        .test('third-nationality-cannot-be-china', () => i18n.t('ICO.PERSONAL.NATIONALITY_DESC'), function (value) {
            const { hasOtherNationality, nationality } = this.parent;
            return !(hasOtherNationality === YES && nationality !== CHINA && value === CHINA);
        })
});

export const hasFormErrors = (errors) => Object.keys(errors || {}).length > 0;

const resetOtherNationalities = (setFieldValue) => {
    setFieldValue('hasOtherNationality', NO);
    setFieldValue('nationality2', '');
    setFieldValue('nationality3', '');
};

const resetPrcIdentityFields = (setFieldValue) => {
    setFieldValue('prcIdNumber', '');
    setFieldValue('chineseLegalName', '');
};

export const updateIdentityFieldsByDocumentType = (documentType, setFieldValue) => {
    if (documentType === EEP) {
        setFieldValue('nationality', CHINA);
        resetOtherNationalities(setFieldValue);
    } else {
        setFieldValue('nationality', '');
    }

    if (documentType !== PASSPORT) {
        setFieldValue('placeOfIssue', '');
    }

    if (documentType !== EEP) {
        resetPrcIdentityFields(setFieldValue);
    }
};

export const updateIdentityFieldsByNationality = (nationality, setFieldValue) => {
    if (nationality === CHINA) {
        resetOtherNationalities(setFieldValue);
        return;
    }

    resetPrcIdentityFields(setFieldValue);
};

const getTrailingNumber = (value) => parseInt(value.slice(-1), 10);

const getOtherNationalitySelectName = (event) => {
    const selectElement = event.currentTarget.parentElement?.parentElement?.querySelector?.('select');

    return selectElement?.name || '';
};

export const handleOtherNationalityAdd = () => {
    addAnotherCount += 1;
    return addAnotherCount;
};

export const handleOtherNationalityRemove = (event, values, setFieldValue) => {
    const selectName = getOtherNationalitySelectName(event);

    if (!selectName) {
        return;
    }

    if (getTrailingNumber(selectName) === 2) {
        setFieldValue('nationality2', values.nationality3);
        setFieldValue('nationality3', '');
    } else {
        setFieldValue(selectName, '');
    }

    addAnotherCount -= 1;
};

const PersonalDetails = () => {
    const initialValues = getUserSessionDataByKey('personal');
    const { t } = useTranslation();
    const lan = i18n.language;
    const titleOptions = DataTitle[lan];
    const history = useHistory();
    const countryList = Country[lan];
    const { isPremier, lang } = getUserSessionDataByKey('global');
    useTagging({
        'page_url': TAG_PAGE_URL,
        'page_language': lang,
        'page_name': TAG_PAGE_NAME,
        'page_subcategory': 'personal information',
        'raw_datalayer': '5745v6',
        'funnel_name': `account opening: ${isPremier ? 'premier' : 'one'}`,
        'funnel_step': '2',
        'funnel_step_name': 'personal information'
    });

    const taggingParams = {
        'page_url': TAG_PAGE_URL,
        'page_language': lang,
        'event_category': 'error',
        'event_action': 'field validation',
        'page_name': TAG_PAGE_NAME,
        'raw_datalayer': '5745v8'
    };

    return (
        <FormLayout>
            <WithI18nProgressIndicator current={1} />
            <Title heading={t('ICO.PERSONAL.TITLE')} legend={t('ICO.PERSONAL.DESC')} />
            <Form
                    initialValues={initialValues}
                    validationSchema={schema}
                    validate={({ dateOfBirth }) => {
                        const errors = {};
                        if (dateOfBirth !== 'incomplete' && timeAtLeast18Years(dateOfBirth)) {
                            errors.dateOfBirth = t('ICO.PERSONAL.DATE_OF_BIRTH_PAST');
                        }
                        return errors;
                    }}
                    onSubmit={({ values }) => {
                        setUserSessionDataByKey('personal', values);
                        history.push('/info/address');
                    }}
                    onChange={rePrevious}
                >
                    {({ values, isSubmitting, isValid, errors, setFieldValue, validateField }) => {
                        if (isSubmitting && !isValid && hasFormErrors(errors)) {
                            validationTagging({ errors, taggingParams });
                        }
                        return (
                            <>
                                <FormFeedback textMap={{ title: t('ICO.COMMON.CORRECT'), of: '/' }} />
                                <ContactDetailsSection t={t} lan={lan} values={values} />
                                <AboutYouSection
                                    t={t}
                                    lan={lan}
                                    values={values}
                                    titleOptions={titleOptions}
                                    countryList={countryList}
                                    setFieldValue={setFieldValue}
                                />
                                <IdentityDetailsSection
                                    t={t}
                                    lan={lan}
                                    lang={lang}
                                    values={values}
                                    countryList={countryList}
                                    setFieldValue={setFieldValue}
                                    validateField={validateField}
                                    onDocumentTypeChange={updateIdentityFieldsByDocumentType}
                                    onNationalityChange={updateIdentityFieldsByNationality}
                                    onOtherNationalityAdd={handleOtherNationalityAdd}
                                    onOtherNationalityRemove={handleOtherNationalityRemove}
                                />
                                <ButtonWithBack />
                            </>
                        );
                    }}
            </Form>
        </FormLayout>
    );
}

export default PersonalDetails;
